/**
 * Trovva — Agent Dashboard
 *
 * "Here's your day" — personal command center for agents.
 * Shows: tasks due, leads needing attention, active deals,
 * upcoming showings, unread messages.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Clock, Phone, MessageSquare, Mail, AlertTriangle,
  ChevronRight, Home, Calendar, Target, DollarSign, User,
  Loader2, ArrowRight, Star, Sparkles, MapPin,
} from 'lucide-react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';
import TrovvaIcon from '../common/TrovvaIcon';

// ── Helpers ──────────────────────────────────────────────────

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const d = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d}d ago`;
}

function formatMoney(val) {
  if (!val) return '$0';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
}

const STATUS_LABEL = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  active_search: 'Active search', offer_submitted: 'Offer submitted',
  offer_accepted: 'Offer accepted', attorney_review: 'Attorney review',
  under_contract: 'Under contract', inspection: 'Inspection',
  appraisal: 'Appraisal', mortgage_commitment: 'Mortgage commitment',
  clear_to_close: 'Clear to close', closed: 'Closed',
};

// ── Main Component ────────────────────────────────────────────

export default function AgentDashboard({ setActiveTab, onBrainOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroInput, setHeroInput] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const r = await fetch('/api/agent-dashboard', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (r.ok) setData(await r.json());
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleHeroSend = (text) => {
    const q = (text || heroInput).trim();
    if (!q) return;
    setHeroInput('');
    onBrainOpen?.(q);
  };

  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Loader2 size={24} color={theme.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
    </div>
  );

  // Fallback to demo data if API not wired yet
  const d = data || {
    agent_name: 'Agent',
    stats: { tasks_overdue: 0, tasks_due_today: 0, new_leads_this_week: 0, active_deals: 0, closing_this_month: 0, pipeline_value: 0, stale_leads: 0, uncontacted_leads: 0 },
    tasks_due: [], stale_leads: [], new_leads: [], active_deals: [], upcoming_showings: [], unread_count: 0,
  };

  const AGENT_PROMPTS = [
    { icon: '✍️', label: 'Draft a follow-up email' },
    { icon: '📋', label: 'What tasks are overdue?' },
    { icon: '🏠', label: 'Summarize my pipeline' },
    { icon: '📝', label: 'NJ disclosure requirements' },
  ];

  const urgentCount = d.stats.tasks_overdue + d.stats.uncontacted_leads + d.stats.stale_leads;

  return (
    <div style={{ padding: '16px 22px', maxWidth: 1100, margin: '0 auto' }}>

      {/* ══════════ BRAIN HERO (Agent version) ══════════ */}
      <div style={{
        background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
        borderRadius: 14, padding: '16px 20px', marginBottom: 16,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrovvaIcon size={20} color="white" animate />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'white', lineHeight: 1.2, marginBottom: 6 }}>
              {greeting()}, {d.agent_name.split(' ')[0]}
              <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.6)', marginLeft: 8 }}>
                {urgentCount > 0 ? `${urgentCount} items need your attention` : "You're all caught up!"}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text" value={heroInput}
                onChange={e => setHeroInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleHeroSend(); }}
                placeholder="Ask Trovva anything…"
                style={{
                  flex: 1, padding: '8px 14px', borderRadius: 8, fontSize: 13,
                  border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)',
                  color: 'white', outline: 'none',
                }}
              />
              <button onClick={() => handleHeroSend()} style={{
                padding: '8px 14px', borderRadius: 8, border: 'none',
                background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600,
                fontSize: 12, cursor: 'pointer',
              }}>Ask</button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10, paddingLeft: 50 }}>
          {AGENT_PROMPTS.map((p, i) => (
            <button key={i} onClick={() => handleHeroSend(p.label)} style={{
              padding: '5px 11px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.18)',
              background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)',
              cursor: 'pointer', fontSize: 11, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 4,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
            >
              <span>{p.icon}</span> {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════ STAT TILES ══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 18 }}>
        <StatTile icon={AlertTriangle} label="Overdue tasks" value={d.stats.tasks_overdue} color={d.stats.tasks_overdue > 0 ? theme.colors.danger : theme.colors.success} onClick={() => setActiveTab?.('leads')} />
        <StatTile icon={Clock} label="Due today" value={d.stats.tasks_due_today} color={theme.colors.warning} />
        <StatTile icon={Star} label="New leads this week" value={d.stats.new_leads_this_week} color={theme.colors.info} onClick={() => setActiveTab?.('leads')} />
        <StatTile icon={Target} label="Active deals" value={d.stats.active_deals} color={theme.colors.primary} onClick={() => setActiveTab?.('transactions')} />
        <StatTile icon={DollarSign} label="Pipeline value" value={formatMoney(d.stats.pipeline_value)} color={theme.colors.success} />
        <StatTile icon={MessageSquare} label="Unread messages" value={d.unread_count} color={d.unread_count > 0 ? theme.colors.accent : theme.colors.textLight} onClick={() => setActiveTab?.('leads')} />
      </div>

      {/* ══════════ TWO-COLUMN LAYOUT ══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>

        {/* ── LEFT: Tasks + Leads ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Tasks Due */}
          <Card title="Today's tasks" icon={CheckCircle} count={d.tasks_due.length} actionLabel="View all" onAction={() => setActiveTab?.('leads')}>
            {d.tasks_due.length === 0 ? (
              <EmptyCard message="No tasks due — nice work!" />
            ) : d.tasks_due.slice(0, 5).map(t => (
              <div key={t.id} style={{
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: `1px solid ${theme.colors.border}40`,
              }}>
                <div style={{
                  width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                  background: t.is_overdue ? theme.colors.danger : theme.colors.warning,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.title}
                  </div>
                  {t.contact_name && (
                    <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 1 }}>
                      {t.contact_name} · {t.is_overdue ? 'overdue' : 'due today'}
                    </div>
                  )}
                </div>
                {t.is_overdue && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: `${theme.colors.danger}15`, color: theme.colors.danger }}>
                    OVERDUE
                  </span>
                )}
              </div>
            ))}
          </Card>

          {/* Leads Needing Attention */}
          <Card title="Leads needing attention" icon={Phone} count={d.stale_leads.length + d.new_leads.length} actionLabel="Open CRM" onAction={() => setActiveTab?.('leads')}>
            {d.new_leads.length === 0 && d.stale_leads.length === 0 ? (
              <EmptyCard message="All leads are up to date!" />
            ) : (
              <>
                {d.new_leads.slice(0, 3).map(l => (
                  <LeadRow key={l.id} lead={l} badge="NEW" badgeColor={theme.colors.info} sub={`Added ${timeAgo(l.created_at)}`} />
                ))}
                {d.stale_leads.slice(0, 4).map(l => (
                  <LeadRow key={l.id} lead={l} badge="STALE" badgeColor={theme.colors.warning} sub={`Last contact: ${timeAgo(l.last_contacted)}`} />
                ))}
              </>
            )}
          </Card>
        </div>

        {/* ── RIGHT: Deals + Showings ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Active Deals */}
          <Card title="Active deals" icon={Home} count={d.active_deals.length} actionLabel="View pipeline" onAction={() => setActiveTab?.('transactions')}>
            {d.active_deals.length === 0 ? (
              <EmptyCard message="No active deals. Convert a lead to get started!" />
            ) : d.active_deals.slice(0, 5).map(deal => (
              <div key={deal.id} style={{
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: `1px solid ${theme.colors.border}40`,
              }}>
                <MapPin size={14} color={theme.colors.primary} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {deal.property_address || deal.contact_name || 'Deal'}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 1 }}>
                    {STATUS_LABEL[deal.status] || deal.status}
                    {deal.offer_price ? ` · ${formatMoney(deal.offer_price)}` : ''}
                    {deal.days_to_close != null && deal.days_to_close >= 0 ? ` · ${deal.days_to_close}d to close` : ''}
                  </div>
                </div>
                {deal.days_to_close != null && deal.days_to_close <= 7 && deal.days_to_close >= 0 && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: `${theme.colors.success}15`, color: theme.colors.success }}>
                    CLOSING SOON
                  </span>
                )}
              </div>
            ))}
          </Card>

          {/* Upcoming Showings */}
          <Card title="Upcoming showings" icon={Calendar} count={d.upcoming_showings.length} actionLabel="View calendar" onAction={() => setActiveTab?.('showings')}>
            {d.upcoming_showings.length === 0 ? (
              <EmptyCard message="No showings scheduled this week." />
            ) : d.upcoming_showings.slice(0, 5).map(s => (
              <div key={s.id} style={{
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: `1px solid ${theme.colors.border}40`,
              }}>
                <Calendar size={14} color={theme.colors.accent} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
                    {s.property_address || 'Showing'}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 1 }}>
                    {s.contact_name ? `${s.contact_name} · ` : ''}
                    {new Date(s.showing_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {s.showing_time ? ` at ${s.showing_time}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function StatTile({ icon: Icon, label, value, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '14px 16px', borderRadius: 10, border: `1px solid ${theme.colors.border}`,
      background: theme.colors.card, textAlign: 'left', cursor: onClick ? 'pointer' : 'default',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: color, borderRadius: '10px 0 0 10px' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Icon size={14} color={color} />
        <span style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: theme.colors.text }}>{value}</div>
    </button>
  );
}

function Card({ title, icon: Icon, count, actionLabel, onAction, children }) {
  return (
    <div style={{
      background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
      borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon size={14} color={theme.colors.primary} />
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>{title}</span>
          {count > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
              background: `${theme.colors.primary}15`, color: theme.colors.primary,
            }}>{count}</span>
          )}
        </div>
        {onAction && (
          <button onClick={onAction} style={{
            fontSize: 11, fontWeight: 600, color: theme.colors.primary,
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            {actionLabel} <ChevronRight size={12} />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function LeadRow({ lead, badge, badgeColor, sub }) {
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Unknown';
  return (
    <div style={{
      padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
      borderBottom: `1px solid ${theme.colors.border}40`,
    }}>
      <User size={14} color={theme.colors.textLight} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
          {name}
          {lead.lead_type ? <span style={{ fontWeight: 400, color: theme.colors.textLight }}> · {lead.lead_type}</span> : ''}
        </div>
        <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 1 }}>{sub}</div>
      </div>
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
        background: `${badgeColor}15`, color: badgeColor,
      }}>
        {badge}
      </span>
      {(lead.phone || lead.email) && (
        <div style={{ display: 'flex', gap: 4 }}>
          {lead.phone && <Phone size={12} color={theme.colors.textLight} />}
          {lead.email && <Mail size={12} color={theme.colors.textLight} />}
        </div>
      )}
    </div>
  );
}

function EmptyCard({ message }) {
  return (
    <div style={{ padding: 24, textAlign: 'center', color: theme.colors.textLight, fontSize: 13 }}>
      <CheckCircle size={20} color={theme.colors.success} style={{ marginBottom: 6 }} />
      <div>{message}</div>
    </div>
  );
}
